import { Box, Text, VStack } from '@chakra-ui/react';
import {
  Button,
  ButtonVariant,
  IconButton,
  Icons,
  SvgSpriteConsumer,
} from '@pluxee-design-system/core';
import { useContractProductLocations } from 'api/contractApi';
import { LocationStatusNameCell } from 'components/Employees/CellRenderers';
import { AssignedLocationState } from 'components/Employees/types';
import { ProductLocationFilterValues } from 'components/Form/LocationSelect/types';
import { initialValues as initialFilterValues } from 'components/Form/LocationSelect/utils';
import { AddressCell, IdCell } from 'components/Locations/CellRenderers';
import LocationProductCell from 'components/Products/CellRenderers/LocationProductCell';
import VirtualizedTable, { ColumnType } from 'components/VirtualizedTable';
import useCurrentContract from 'data/useCurrentContract';
import useDeepMemo from 'data/useDeepMemo';
import { ProductLocationsSortingEnum } from 'generated';
import useTranslations from 'i18n';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import AutoSizer, { Size } from 'react-virtualized-auto-sizer';
import { ChangeLocations, LocationsFormValues } from '../types';
import { getChangedLocations, hasLocationsChanged } from './utils';

interface LocationsStepProps {
  isEditable: boolean;
  productId: string;
  onBack?: () => void;
  onSubmit: (toAdd: string[], toRemove: string[]) => void;
  selectedLocations?: ChangeLocations;
}

const getInitialValues = (selectedLocations?: ChangeLocations) => {
  const result: Record<string, AssignedLocationState> = {};

  (selectedLocations?.toAdd || []).forEach((key) => {
    if (!result[key]) {
      result[key] = { oldState: undefined, newState: true };
    }
    result[key].newState = true;
  });

  (selectedLocations?.toRemove || []).forEach((key) => {
    if (!result[key]) {
      result[key] = { oldState: undefined, newState: false };
    }
    result[key].newState = false;
  });

  return result;
};

const LocationsStep = ({
  isEditable,
  productId,
  onBack,
  onSubmit,
  selectedLocations,
}: LocationsStepProps) => {
  const methods = useForm<LocationsFormValues>({
    defaultValues: useMemo(
      () => ({ locations: getInitialValues(selectedLocations) }),
      [selectedLocations],
    ),
  });
  const handleSubmit = useCallback(
    ({ locations }: LocationsFormValues) => {
      const { toAdd, toRemove } = getChangedLocations(locations);
      onSubmit(toAdd, toRemove);
    },
    [onSubmit],
  );
  const values = methods.watch();

  const { t } = useTranslations();
  const { contractId } = useCurrentContract();
  const [filterState, setFilterState] = useState<ProductLocationFilterValues>(initialFilterValues);
  const updateFilterState = useCallback(
    async (newState: ProductLocationFilterValues) => setFilterState(newState),
    [setFilterState],
  );
  const {
    data: locations,
    hasNextPage,
    isEmpty,
    isLoadingPage,
    loadNextPage,
  } = useContractProductLocations(contractId, productId, false, filterState);
  const hasChangedLocations = useDeepMemo(() => hasLocationsChanged(values), [values]);

  const columns = useMemo<ColumnType[]>(
    () => [
      {
        name: '',
        id: 'select',
        component: LocationProductCell,
        size: 14,
        sortable: false,
        align: 'center',
        extra: isEditable,
      },
      {
        name: t('locations.table.name', 'Name'),
        id: 'location.name',
        component: LocationStatusNameCell,
        size: '200px',
        sortId: ProductLocationsSortingEnum.Name,
        sortable: true,
      },
      {
        name: t('locations.table.tableId', 'ID'),
        id: 'location.masterId',
        component: IdCell,
        size: '210px',
        sortId: ProductLocationsSortingEnum.MasterId,
        sortable: true,
      },
      {
        name: t('locations.table.street', 'Street'),
        id: 'location.address.street',
        component: AddressCell,
        size: '130px',
        sortId: ProductLocationsSortingEnum.Street,
        sortable: false,
      },
      {
        name: t('locations.table.city', 'City'),
        id: 'location.address.city',
        size: '130px',
        sortId: ProductLocationsSortingEnum.City,
        sortable: false,
      },
    ],
    [isEditable, t],
  );

  return (
    <FormProvider {...methods}>
      {onBack && (
        <Box position="absolute" top={6}>
          <IconButton
            aria-label={t('global_texts.sidebarNavigation.back', 'Back')}
            variant={ButtonVariant.ICON}
            icon={<SvgSpriteConsumer svgId={Icons.ARROW_LEFT24} size={24} />}
            onClick={onBack}
          />
        </Box>
      )}
      <Box>
        <Text variant="title.desktopCard" mt={10}>
          {t('products_portal.sidebar_texts.locationsHeader', 'Choose locations for your product')}
        </Text>
        <Box as="form" mt={6} minHeight="550px" id="LocationStep_form">
          <AutoSizer disableWidth defaultHeight={550}>
            {({ height }: Size) => (
              <VirtualizedTable
                columns={columns}
                filterState={filterState}
                hasNextPage={hasNextPage}
                isEmpty={isEmpty}
                isNextPageLoading={isLoadingPage}
                loadNextPage={loadNextPage}
                items={locations}
                itemSize={73}
                height={height}
                onSortBy={updateFilterState}
                pageSize={20}
                emptyDataText={t(
                  'products_portal.sidebar_texts.noLocationsAvailable',
                  'No location has currently assigned the adequate activity',
                )}
                reachedEndText={t('global_texts.labels.reachedEnd', 'Displayed all results')}
              />
            )}
          </AutoSizer>
        </Box>
      </Box>
      {isEditable && (
        <VStack
          position="sticky"
          spacing={6}
          bottom={0}
          background="semantic.surface.1"
          py={6}
          mb={-6}
        >
          <Button
            form="LocationStep_form"
            onClick={methods.handleSubmit(handleSubmit)}
            isDisabled={!hasChangedLocations}
            type="button"
            variant="primaryFilled"
            id="LocationsStep_applyButton"
          >
            {t('products.sidebarButtons.apply', 'Apply')}
          </Button>
        </VStack>
      )}
    </FormProvider>
  );
};

export default LocationsStep;
